import { useRef, useState, useEffect } from 'react';
import { Box, Button, Paper } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import * as XLSX from 'xlsx-js-style';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';

function ESIReport() {
    const location = useLocation();
    const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
    const { companyName, addressLine1, addressLine2 } = getCompanyDisplayFromDetails(companyDetails || {});
    let ESITableRef = useRef(null);
    const { empDetails, esiDetail } = location.state;
    const [companyContriPercentage, setCompanyContriPercentage] = useState(0.0325); // Default value

    /** Fetch the dynamic company contribution percentage on component mount */
    useEffect(() => {
        const fetchEsicCompanyContribution = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employee-master/esic-comp-contri`);
                
                if (res.data && res.data.length > 0 && res.data[0].companyContriPercentage) {
                    setCompanyContriPercentage(res.data[0].companyContriPercentage);
                }
            } catch (error) {
                console.error("Failed to fetch ESI company contribution:", error);
            }
        };

        fetchEsicCompanyContribution();
    }, []);

    const formatAmountOrBlank = (value) => {
        const num = Number(value) || 0;
        if (num === 0) return '';
        return num.toLocaleString('en-IN');
    };

    /** Company contribution only when employee ESI > 0; otherwise 0 */
    const getCompanyContribution = (esiContribution, grossSalary) => {
        const esi = Number(esiContribution) || 0;
        if (esi <= 0) return 0;
        return Math.ceil((Number(grossSalary) || 0) * companyContriPercentage);
    };

    // Only employees with ESI contribution > 0 (zeros need not show)
    const empFilteredDetails = (empDetails || [])
        .filter((empDetail) => (Number(empDetail.Esic) || 0) > 0)
        .map((empDetail, index) => {
            const esiContribution = Number(empDetail.Esic) || 0;
            const grossSalary = Number(empDetail.Gross) || 0;
            return ({
                Sr_No: index,
                Employee_Code: empDetail.employee_code,
                Name: `${empDetail.first_name || ''} ${empDetail.last_name || ''}`.trim(),
                ESI_Number: empDetail.ESINumber,
                Gross_Salary: grossSalary,
                ESI_Contribution: esiContribution,
                Company_Contribution: getCompanyContribution(esiContribution, grossSalary),
            });
        });

    let total_Gross_Sal = 0;
    let total_ESI_Contribution = 0;
    let total_Company_Contribution = 0;

    const month = empDetails?.[0]?.payroll_month || '';
    const year = empDetails?.[0]?.payroll_year || '';
    
    const handleGenerateExcel = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [];
        const headerNames = ['Sr. no', 'Employee Code', 'Name', 'ESI Number', 'Gross Salary (INR)', 'ESI Contribution (INR)', 'Company Contribution (INR)'];
        let totalGross = 0, totalESI = 0, totalCompany = 0;
        const fullDataRows = [];

        empFilteredDetails.forEach((curElem) => {
            totalGross += curElem.Gross_Salary;
            totalESI += curElem.ESI_Contribution;
            totalCompany += curElem.Company_Contribution;
            fullDataRows.push([
                curElem.Sr_No + 1,
                curElem.Employee_Code,
                curElem.Name,
                curElem.ESI_Number,
                curElem.Gross_Salary || '',
                curElem.ESI_Contribution || '',
                curElem.Company_Contribution || ''
            ]);
        });

        const isZeroOrNull = (v) => v == null || v === '' || (typeof v === 'number' && !isNaN(v) && Number(v) === 0);
        const visibleColIndices = [0, 1, 2];
        for (let c = 3; c < 7; c++) {
            const hasNonZero = fullDataRows.some(row => !isZeroOrNull(row[c]));
            if (hasNonZero) visibleColIndices.push(c);
        }
        const numCols = visibleColIndices.length;
        const pick = (row) => visibleColIndices.map(c => row[c]);

        wsData.push([companyName || '', ...Array(numCols - 1).fill('')]);
        wsData.push([addressLine1 || '', ...Array(numCols - 1).fill('')]);
        wsData.push([addressLine2 || '', ...Array(numCols - 1).fill('')]);
        wsData.push([`ESI details for ${month} ${year}`, ...Array(numCols - 1).fill('')]);
        wsData.push(pick(headerNames));
        fullDataRows.forEach(row => wsData.push(pick(row)));
        wsData.push(pick(['', '', 'GRAND TOTAL', '', totalGross || '', totalESI || '', totalCompany || '']));
        // Combined total (EE ESI + Company ESI) — same value as API esi_total
        const combinedEsiTotal = totalESI + totalCompany;
        const totalRow = Array(numCols).fill('');
        totalRow[0] = '';
        totalRow[Math.min(2, numCols - 1)] = 'TOTAL';
        totalRow[numCols - 1] = combinedEsiTotal || '';
        wsData.push(totalRow);
        
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        const calculateColumnWidths = (data) => {
            const colWidths = [];
            const cols = data[0]?.length || 1;
            for (let col = 0; col < cols; col++) {
                let maxWidth = 0;
                
                data.forEach((row, rowIndex) => {
                    if (rowIndex < 4) return; // Skip header rows (now 4 rows instead of 5)
                    
                    if (row[col] !== undefined && row[col] !== null) {
                        const cellValue = String(row[col]);
                        maxWidth = Math.max(maxWidth, cellValue.length);
                    }
                });
                
                colWidths.push({ wch: Math.max(maxWidth + 1, 10) });
            }
            
            return colWidths;
        };
        
        ws['!cols'] = calculateColumnWidths(wsData);
        
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
            { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
            { s: { r: 3, c: 0 }, e: { r: 3, c: numCols - 1 } }
        ];
        
        // Common border style
        const borderStyle = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
        };

        // Style for company name header
        const companyNameStyle = {
            font: { bold: true, sz: 16, color: { rgb: "000000" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borderStyle
        };

        // Style for column headers (lighter blue)
        const columnHeaderStyle = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "5B9BD5" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borderStyle
        };
        
        const dataStyle = {
            border: borderStyle,
            alignment: { vertical: "center" }
        };
        
        // Apply styles to company header rows (rows 1-4)
        for (let row = 0; row <= 3; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            if (ws[cellAddress]) {
                ws[cellAddress].s = companyNameStyle;
            }
            
            // Remove empty cells in merged range (to prevent visible lines)
            for (let col = 1; col < 7; col++) {
                const emptyCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[emptyCellAddress]) {
                    delete ws[emptyCellAddress];
                }
            }
        }
        
        // Apply styles to column headers (row 5)
        for (let col = 0; col < 7; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 4, c: col });
            if (ws[cellAddress]) {
                ws[cellAddress].s = columnHeaderStyle;
            }
        }
        
        // Apply styles to data cells
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let row = 5; row <= range.e.r; row++) {
            for (let col = 0; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress]) {
                    const isGrandTotal = row === range.e.r - 1;
                    const isCombinedTotal = row === range.e.r;
                    ws[cellAddress].s = {
                        ...dataStyle,
                        font: { bold: isGrandTotal || isCombinedTotal },
                        fill: isCombinedTotal ? { fgColor: { rgb: "D4EDDA" } } : undefined,
                        alignment: {
                            vertical: "center",
                            horizontal: col >= 4 || isCombinedTotal ? "right" : (col === 0 ? "center" : "left")
                        }
                    };

                    // Format numbers with Indian comma style
                    if (col >= 4 || (isCombinedTotal && col === range.e.c)) {
                        ws[cellAddress].z = '#,##0';
                    }
                }
            }
        }
        
        XLSX.utils.book_append_sheet(wb, ws, 'ESI Report');
        XLSX.writeFile(wb, `${getShortCompanyName(companyName) || 'Report'} Report for ESI Statement ${month.substring(0, 3)} ${year}.xlsx`);
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "10px", overflowX: "hidden", maxWidth: "100vw" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", padding: '20px', minWidth: 0, overflow: "hidden" }}>
                <Button
                    variant='contained'
                    color='success'
                    onClick={handleGenerateExcel}
                    sx={{ marginBottom: 2 }}
                >
                    Download ESI Report (excel)
                </Button>

                <div
                    style={{
                        height: '600px',
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: '#fff'
                    }}
                >
                <table
                    ref={ESITableRef}
                    style={{
                        display: 'table',
                        borderCollapse: 'collapse',
                        width: '100%',
                        tableLayout: 'fixed',
                        margin: 0
                    }}
                >
                    <colgroup>
                        <col style={{ width: '4%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '18%' }} />
                    </colgroup>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: 'white', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                        <tr>
                            <th style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>Sr. no</th>
                            <th style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>Employee Code</th>
                            <th style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>Name</th>
                            <th style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>ESI Number</th>
                            <th style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>Gross Salary (INR)</th>
                            <th style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>ESI Contribution (INR)</th>
                            <th style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>Company Contribution (INR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            empFilteredDetails.map((curElem) => {
                                total_Gross_Sal += curElem.Gross_Salary;
                                total_ESI_Contribution += curElem.ESI_Contribution;
                                total_Company_Contribution += curElem.Company_Contribution;

                                return (
                                    <tr key={curElem.Sr_No} style={{ backgroundColor: 'white' }}>
                                        <td style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{curElem.Sr_No + 1}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{curElem.Employee_Code}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{curElem.Name}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{curElem.ESI_Number}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{formatAmountOrBlank(curElem.Gross_Salary)}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{formatAmountOrBlank(curElem.ESI_Contribution)}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{formatAmountOrBlank(curElem.Company_Contribution)}</td>
                                    </tr>
                                );
                            })
                        }
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                            <td style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}></td>
                            <td style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}></td>
                            <td style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>GRAND TOTAL</td>
                            <td style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}></td>
                            <td style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{formatAmountOrBlank(total_Gross_Sal)}</td>
                            <td style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{formatAmountOrBlank(total_ESI_Contribution)}</td>
                            <td style={{ padding: '8px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.9rem' }}>{formatAmountOrBlank(total_Company_Contribution)}</td>
                        </tr>
                        <tr style={{ backgroundColor: '#d4edda', fontWeight: 'bold' }}>
                            <td colSpan={6} style={{ padding: '12px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.95rem' }}>
                                TOTAL (ESI Contribution + Company Contribution) (₹)
                            </td>
                            <td style={{ padding: '12px 6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontSize: '0.95rem' }}>
                                {formatAmountOrBlank(total_ESI_Contribution + total_Company_Contribution)}
                            </td>
                        </tr>
                    </tbody>
                </table>
                </div>
            </Box>
        </Box>
    );
}

export default ESIReport;
